import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import { TOOL_GROUP_ID, type Cornerstone } from "@/lib/viewer/cornerstone";
import {
  ALL_TOOLS,
  orientationLetters,
  readMeasurement,
  screenAxes,
  type ViewGeometry,
  type ImagePlane,
  type Measurement,
  type OrientationLetters,
  type RoomTool,
  type Vec3,
} from "@/lib/viewer/room";
import { rangeOf, widthCentreOf } from "@/lib/viewer/windows";

/** What the viewport reports upward, every time any of it changes. */
export interface ViewportState {
  ready: boolean;
  index: number;
  voi: { width: number; centre: number } | null;
  zoom: number;
  /** The scalar under the cursor -- Hounsfield on CT -- or null. */
  value: number | null;
  modality: string | null;
  thickness: number | null;
  inverted: boolean;
  viewportId: string | null;
  geometry: ViewGeometry | null;
  letters: OrientationLetters | null;
  /** The clip's FrameTime in ms, from the file; null on a still. */
  frameTime: number | null;
}

export const EMPTY_VIEWPORT: ViewportState = {
  ready: false,
  index: 0,
  voi: null,
  zoom: 1,
  value: null,
  modality: null,
  thickness: null,
  inverted: false,
  viewportId: null,
  geometry: null,
  letters: null,
  frameTime: null,
};

export interface ViewportHandle {
  step: (delta: number) => void;
  goTo: (index: number) => void;
  applyWindow: (width: number, centre: number) => void;
  toggleInvert: () => void;
  rotate: (degrees: number) => void;
  flip: (axis: "h" | "v") => void;
  reset: () => void;
  /** The plane of the image on screen, from its header. */
  plane: () => ImagePlane | null;
  /** A patient-space point on this viewport's canvas, in CSS pixels. */
  toCanvas: (p: Vec3) => [number, number] | null;
}

/** The room's one rendering engine, which every stack cell joins. */
export interface RoomEngine {
  cs: Cornerstone;
  engine: {
    id: string;
    enableElement: (input: unknown) => void;
    disableElement: (viewportId: string) => void;
    getViewport: (id: string) => unknown;
    resize: (immediate?: boolean, keepCamera?: boolean) => void;
    renderViewports: (ids: string[]) => void;
    destroy: () => void;
  };
}

type Vp = {
  setStack: (ids: string[], i?: number) => Promise<void>;
  render: () => void;
  getProperties: () => { voiRange?: { lower: number; upper: number }; invert?: boolean };
  setProperties: (p: unknown) => void;
  resetProperties: () => void;
  resetCamera: () => void;
  getZoom?: () => number;
  getCurrentImageIdIndex: () => number;
  setImageIdIndex: (i: number) => Promise<void> | void;
  getViewPresentation: () => { rotation?: number; flipHorizontal?: boolean; flipVertical?: boolean };
  setViewPresentation: (p: { rotation?: number; flipHorizontal?: boolean; flipVertical?: boolean }) => void;
  canvasToWorld: (p: [number, number]) => [number, number, number];
  worldToCanvas: (p: [number, number, number]) => [number, number];
  getCamera: () => { focalPoint?: number[]; viewPlaneNormal?: number[] };
  getFrameOfReferenceUID: () => string | undefined;
  getImageData: () => { imageData?: unknown } | undefined;
};

let seq = 0;

/**
 * One reading-room stack viewport, keyed by the caller on the SERIES.
 *
 * The same engine as `DiagnosticViewer`'s Stage and the same rules --
 * the scalar under the cursor already has the modality LUT applied, the
 * readouts follow Cornerstone's events rather than our setters, the tool
 * group is the app's shared one -- but its chrome lives in the reading
 * room, so this renders nothing but the canvas host.
 *
 * ONE RENDERING ENGINE FOR EVERY CELL, owned by the room. Cornerstone's
 * slice synchroniser looks the source viewport up in the TARGET's engine,
 * so two cells on two engines cannot be linked at all -- it returns
 * without an error and nothing follows.
 *
 * MEASUREMENTS ARE REPORTED FOR THE WHOLE CASE, not this series:
 * `caseImageIds` is every diagnostic image the room holds, and the
 * annotation state is global, so a length drawn on series 2 stays in the
 * panel after the reader moves to series 3.
 */
export const RoomViewport = forwardRef<
  ViewportHandle,
  {
    imageIds: string[];
    caseImageIds: string[];
    tool: RoomTool;
    getEngine: () => Promise<RoomEngine>;
    onState: (state: ViewportState) => void;
    onMeasurements: (list: Measurement[]) => void;
    onUnavailable: (reason: string) => void;
  }
>(function RoomViewport(
  { imageIds, caseImageIds, tool, getEngine, onState, onMeasurements, onUnavailable },
  ref,
) {
  const elementRef = useRef<HTMLDivElement | null>(null);
  const csRef = useRef<Cornerstone | null>(null);
  const vpRef = useRef<Vp | null>(null);
  const roomRef = useRef<RoomEngine | null>(null);
  const [state, setState] = useState<ViewportState>(EMPTY_VIEWPORT);

  const patch = useCallback((p: Partial<ViewportState>) => {
    setState((prev) => ({ ...prev, ...p }));
  }, []);

  useEffect(() => {
    onState(state);
  }, [state, onState]);

  // ------------------------------------------------------------------
  // Mount: join the room's engine, load the stack.
  // ------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    const element = elementRef.current;
    if (!element) return;
    const viewportId = `rr-vp-${++seq}`;
    let joined: RoomEngine | null = null;

    (async () => {
      let room: RoomEngine;
      try {
        room = await getEngine();
      } catch (err) {
        const why = err instanceof Error && err.message ? err.message : "The browser gave no reason.";
        console.error("reading room: init failed", err);
        if (!cancelled) onUnavailable(why);
        return;
      }
      if (cancelled) return;
      const { cs, engine } = room;
      csRef.current = cs;
      roomRef.current = room;

      engine.enableElement({
        viewportId,
        type: cs.core.Enums.ViewportType.STACK,
        element: element as HTMLDivElement,
      });
      joined = room;
      const vp = engine.getViewport(viewportId) as Vp;
      vpRef.current = vp;
      cs.tools.ToolGroupManager.getToolGroup(TOOL_GROUP_ID)?.addViewport(viewportId, engine.id);

      try {
        await vp.setStack(imageIds, 0);
      } catch {
        if (!cancelled) onUnavailable("These images could not be decoded as DICOM.");
        return;
      }
      if (cancelled) return;
      vp.render();

      const series = cs.core.metaData.get("generalSeriesModule", imageIds[0]) as
        | { modality?: string }
        | undefined;
      // The camera's normal is a fact about the IMAGE only when the header
      // says how it was oriented. An ultrasound frame does not, and the
      // default camera would otherwise colour it as an axial acquisition
      // and offer it as a plane to cut reference lines through.
      const plane = cs.core.metaData.get("imagePlaneModule", imageIds[0]) as
        | { rowCosines?: number[] | null }
        | undefined;
      const normal = plane?.rowCosines ? vp.getCamera().viewPlaneNormal : undefined;
      patch({
        ready: true,
        viewportId,
        modality: series?.modality ?? null,
        geometry: {
          frameOfReference: vp.getFrameOfReferenceUID() ?? null,
          normal: normal && normal.length >= 3 ? ([normal[0], normal[1], normal[2]] as Vec3) : null,
        },
      });
    })();

    return () => {
      cancelled = true;
      if (!joined) return;
      try {
        joined.cs.tools.ToolGroupManager.getToolGroup(TOOL_GROUP_ID)?.removeViewports(
          joined.engine.id,
          viewportId,
        );
      } catch {
        /* the group may already be gone */
      }
      try {
        joined.engine.disableElement(viewportId);
      } catch {
        /* the room's engine was destroyed first */
      }
      vpRef.current = null;
    };
    // Keyed on the series by the caller: a new stack is a new mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------------------------------------------------------
  // Readouts, from Cornerstone's own events -- a right-drag we never see
  // must move the W/L numbers exactly as F3 does.
  // ------------------------------------------------------------------
  useEffect(() => {
    const element = elementRef.current;
    const cs = csRef.current;
    if (!element || !cs || !state.ready) return;

    const readVoi = () => {
      const props = vpRef.current?.getProperties();
      if (props?.voiRange) patch({ voi: widthCentreOf(props.voiRange) });
    };
    // LETTERS ONLY WHERE THE HEADER HAS AN ORIENTATION. An ultrasound
    // frame has none, and Cornerstone still gives the viewport a default
    // camera -- so measuring it would print a confident A, P, R and L on
    // a picture whose orientation nobody recorded.
    const oriented = () => {
      const vp = vpRef.current;
      const imageId = vp ? imageIds[vp.getCurrentImageIdIndex()] : undefined;
      const plane = imageId
        ? (cs.core.metaData.get("imagePlaneModule", imageId) as { rowCosines?: number[] | null } | undefined)
        : undefined;
      return !!plane?.rowCosines;
    };
    const readCamera = () => {
      const vp = vpRef.current;
      if (!vp) return;
      const axes = oriented() ? screenAxes(vp) : null;
      patch({
        zoom: vp.getZoom?.() ?? 1,
        letters: axes ? orientationLetters(axes.right, axes.up) : null,
      });
    };
    const readIndex = (event: Event) => {
      const detail = (event as CustomEvent<{ imageIdIndex?: number; imageId?: string }>).detail;
      if (typeof detail?.imageIdIndex !== "number") return;
      const imageId = detail.imageId ?? imageIds[detail.imageIdIndex];
      const plane = imageId
        ? (cs.core.metaData.get("imagePlaneModule", imageId) as { sliceThickness?: number } | undefined)
        : undefined;
      const cine = imageId && imageId.includes("frame=")
        ? (cs.core.metaData.get("cineModule", imageId) as { frameTime?: number } | undefined)
        : undefined;
      patch({
        index: detail.imageIdIndex,
        thickness: typeof plane?.sliceThickness === "number" ? plane.sliceThickness : null,
        frameTime: typeof cine?.frameTime === "number" && cine.frameTime > 0 ? cine.frameTime : null,
      });
      readCamera();
    };

    const { Events } = cs.core.Enums;
    element.addEventListener(Events.VOI_MODIFIED, readVoi);
    element.addEventListener(Events.CAMERA_MODIFIED, readCamera);
    element.addEventListener(Events.STACK_NEW_IMAGE, readIndex);
    element.addEventListener(Events.IMAGE_RENDERED, readVoi);
    readVoi();
    readCamera();
    readIndex(new CustomEvent("x", { detail: { imageIdIndex: vpRef.current?.getCurrentImageIdIndex() ?? 0 } }));
    return () => {
      element.removeEventListener(Events.VOI_MODIFIED, readVoi);
      element.removeEventListener(Events.CAMERA_MODIFIED, readCamera);
      element.removeEventListener(Events.STACK_NEW_IMAGE, readIndex);
      element.removeEventListener(Events.IMAGE_RENDERED, readVoi);
    };
  }, [state.ready, imageIds, patch]);

  // ------------------------------------------------------------------
  // Measurements: the global annotation state, narrowed to this case.
  // ------------------------------------------------------------------
  useEffect(() => {
    const cs = csRef.current;
    if (!cs || !state.ready) return;
    const ours = new Set(caseImageIds);
    const read = () => {
      const all = cs.tools.annotation.state.getAllAnnotations() as Parameters<typeof readMeasurement>[0][];
      onMeasurements(
        all
          .map(readMeasurement)
          .filter((m): m is Measurement => m !== null && ours.has(m.imageId)),
      );
    };
    const { Events } = cs.tools.Enums;
    const target = cs.core.eventTarget;
    const names = [
      Events.ANNOTATION_COMPLETED,
      Events.ANNOTATION_MODIFIED,
      Events.ANNOTATION_REMOVED,
    ];
    for (const n of names) target.addEventListener(n, read);
    // AND ON RENDER. A tool computes its statistics while drawing and
    // announces them only when the annotation was marked stale, so the
    // COMPLETED event can arrive before the numbers exist -- the panel
    // then showed "Length —" for a ruler the image had measured.
    const element = elementRef.current;
    element?.addEventListener(Events.ANNOTATION_RENDERED, read);
    read();
    return () => {
      for (const n of names) target.removeEventListener(n, read);
      element?.removeEventListener(Events.ANNOTATION_RENDERED, read);
    };
  }, [state.ready, caseImageIds, onMeasurements]);

  // ------------------------------------------------------------------
  // The value under the cursor. Nothing is applied to the scalar: the
  // modality LUT is already in it (the Phase 0 trap).
  // ------------------------------------------------------------------
  const onMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const vp = vpRef.current;
      if (!vp) return;
      const rect = event.currentTarget.getBoundingClientRect();
      try {
        const world = vp.canvasToWorld([event.clientX - rect.left, event.clientY - rect.top]);
        const imageData = vp.getImageData()?.imageData as
          | {
              worldToIndex: (w: number[]) => number[];
              getDimensions: () => number[];
              getPointData: () => {
                getScalars: () => { getData: () => ArrayLike<number>; getNumberOfComponents: () => number };
              };
            }
          | undefined;
        if (!imageData) return;
        const [x, y] = imageData.worldToIndex(world).map((n) => Math.round(n));
        const [cols, rows] = imageData.getDimensions();
        if (x == null || y == null || cols == null || rows == null || x < 0 || y < 0 || x >= cols || y >= rows) {
          patch({ value: null });
          return;
        }
        const scalars = imageData.getPointData().getScalars();
        // A colour image has three samples a pixel and no single value to
        // give -- an ultrasound Doppler frame, say. Saying nothing beats
        // reading the red channel of the wrong pixel.
        if (scalars.getNumberOfComponents() !== 1) {
          patch({ value: null });
          return;
        }
        const v = scalars.getData()[y * cols + x];
        patch({ value: typeof v === "number" ? Math.round(v) : null });
      } catch {
        patch({ value: null });
      }
    },
    [patch],
  );

  // ------------------------------------------------------------------
  // Tools. Every left-button tool goes passive -- which strips ONLY the
  // primary binding, so window/level keeps the right button, pan the
  // middle and scroll the wheel -- then the chosen one takes the left.
  // The group is shared by every cell, so re-asserting it from each is
  // harmless; handing it back is the room's job, not a cell's, or the
  // first cell to unmount would take the reader's tool away from the
  // cells still on screen.
  // ------------------------------------------------------------------
  useEffect(() => {
    const cs = csRef.current;
    const group = cs?.tools.ToolGroupManager.getToolGroup(TOOL_GROUP_ID);
    if (!cs || !group || !state.ready) return;
    const chosen = ALL_TOOLS.find((t) => t.id === tool);
    if (!chosen) return;
    for (const t of ALL_TOOLS) {
      if (t.cs !== chosen.cs) group.setToolPassive(t.cs);
    }
    group.setToolActive(chosen.cs, {
      bindings: [{ mouseButton: cs.tools.Enums.MouseBindings.Primary }],
    });
  }, [state.ready, tool]);

  // Cornerstone sizes its canvas from the element.
  useEffect(() => {
    const element = elementRef.current;
    if (!element || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => roomRef.current?.engine.resize(true, true));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      step: (delta) => {
        const vp = vpRef.current;
        if (!vp) return;
        const next = Math.max(0, Math.min(imageIds.length - 1, vp.getCurrentImageIdIndex() + delta));
        void vp.setImageIdIndex(next);
      },
      goTo: (i) => {
        void vpRef.current?.setImageIdIndex(Math.max(0, Math.min(imageIds.length - 1, i)));
      },
      applyWindow: (width, centre) => {
        const vp = vpRef.current;
        if (!vp) return;
        vp.setProperties({ voiRange: rangeOf({ width, centre }) });
        vp.render();
      },
      toggleInvert: () => {
        const vp = vpRef.current;
        if (!vp) return;
        const next = !vp.getProperties().invert;
        vp.setProperties({ invert: next });
        vp.render();
        patch({ inverted: next });
      },
      rotate: (degrees) => {
        const vp = vpRef.current;
        if (!vp) return;
        const current = vp.getViewPresentation().rotation ?? 0;
        vp.setViewPresentation({ rotation: (current + degrees + 360) % 360 });
        vp.render();
      },
      flip: (axis) => {
        const vp = vpRef.current;
        if (!vp) return;
        const pres = vp.getViewPresentation();
        vp.setViewPresentation(
          axis === "h"
            ? { flipHorizontal: !pres.flipHorizontal }
            : { flipVertical: !pres.flipVertical },
        );
        vp.render();
      },
      reset: () => {
        const vp = vpRef.current;
        if (!vp) return;
        vp.resetProperties();
        vp.resetCamera();
        vp.setViewPresentation({ rotation: 0, flipHorizontal: false, flipVertical: false });
        vp.render();
        patch({ inverted: false });
      },
      plane: () => {
        const vp = vpRef.current;
        const cs = csRef.current;
        const imageId = vp ? imageIds[vp.getCurrentImageIdIndex()] : undefined;
        if (!cs || !imageId) return null;
        const m = cs.core.metaData.get("imagePlaneModule", imageId) as
          | {
              imagePositionPatient?: number[];
              rowCosines?: number[];
              columnCosines?: number[];
              rows?: number;
              columns?: number;
              rowPixelSpacing?: number;
              columnPixelSpacing?: number;
            }
          | undefined;
        const v3 = (a?: number[]): Vec3 | null =>
          a && a.length >= 3 ? [Number(a[0]), Number(a[1]), Number(a[2])] : null;
        const position = v3(m?.imagePositionPatient);
        const rowCosines = v3(m?.rowCosines);
        const columnCosines = v3(m?.columnCosines);
        if (!m || !position || !rowCosines || !columnCosines || !m.rows || !m.columns) return null;
        return {
          position,
          rowCosines,
          columnCosines,
          rows: m.rows,
          columns: m.columns,
          rowSpacing: m.rowPixelSpacing ?? 1,
          columnSpacing: m.columnPixelSpacing ?? 1,
        };
      },
      toCanvas: (p) => {
        const c = vpRef.current?.worldToCanvas([p[0], p[1], p[2]]);
        return c && Number.isFinite(c[0]) && Number.isFinite(c[1]) ? [c[0], c[1]] : null;
      },
    }),
    [imageIds.length, patch],
  );

  return (
    <div
      ref={elementRef}
      data-cy="rr-canvas"
      className="rr-canvas"
      onMouseMove={onMove}
      onMouseLeave={() => patch({ value: null })}
      onContextMenu={(e) => e.preventDefault()}
    />
  );
});
